import { Permission } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
import { registerClientTools, type ClientTool, type ToolOutcome } from '@vitesse/base-ui/ai';
import type { GatewayEntry } from '../managers/gateway-entry';
import { GATEWAY_PARAM, resolveGateway, type GatewayScope } from './gateway-resolve';
import { categorize } from '../shell/vitesse-value-editor';
import type { StreamFrame } from '../../core/bridge/types';
import type { BackendClient } from '../bridge/backend-client';
import type { TypedValue } from '../../core/bridge/connect-client';

/*
 * WHAT THE ASSISTANT MAY DO TO A PLC.
 *
 * These are the tools that touch the machine, and everything about their shape follows from that.
 *
 * THEY RUN IN THIS BROWSER, ON THIS PERSON'S SESSION. That is the security model, not a detail of
 * where the code sits: `writeSymbols` is authorized by the api-gateway against the caller's own
 * permissions exactly as if they had typed the value into the grid. The assistant cannot reach past
 * its operator, and an org that has not granted symbol-write does not acquire it by asking the AI.
 *
 * A LOCK IS THE PLUGIN'S TO ENFORCE, AND NOBODY ELSE'S. A locked symbol rejects writes at the
 * plugin with `LOCKED`; api-gateway is stateless about it and so is this. So the tools SURFACE the
 * lock — every row says whether it is locked, and a refused write says which symbol and why — and
 * there is deliberately NO unlock tool. Unlocking is a person's decision about a machine, and an
 * assistant that could clear its own obstacle is not gated by one.
 *
 * READS ARE CHEAP AND WRITES ARE NOT. Search and read are ordinary. Write is `mutates`, it names
 * every symbol it touched in the outcome, and it is a natural candidate for the Ask tier — an
 * administrator can set `symbols.write` to require a person's confirmation, and the panel will ask
 * before the call runs rather than after.
 */

/** How the tools reach a gateway — `gateway` on each call, resolved against the whole roster. */
export interface SymbolToolDeps extends GatewayScope {
  /** The worker client — org-wide, addressed per call by gateway id (see gateway-resolve.ts). */
  backend(): BackendClient | undefined;
}

const noBackend: ToolOutcome = { ok: false, message: 'The backend is not ready yet — try again in a moment.' };

/** Enough rows to answer a question, few enough to fit a prompt. A search that returns four hundred
 *  symbols has not helped; it has moved the filtering into the model. */
const MAX_ROWS = 50;

/** Resolve a controller by id OR by name, because a person says "Press 2" and an assistant repeating
 *  them should not have to have looked up an id first. */
function findController(entry: GatewayEntry, idOrName: string) {
  const wanted = idOrName.trim().toLowerCase();
  return entry.controllers.all.find((c) => c.id.toLowerCase() === wanted || c.name.toLowerCase() === wanted);
}

function controllerMiss(entry: GatewayEntry, idOrName: string): ToolOutcome {
  const known = entry.controllers.all.map((c) => `${c.name} (${c.id})`);
  return {
    ok: false,
    message: `No controller "${idOrName}" on this relay.${known.length ? ` The controllers here are: ${known.join(', ')}.` : ' This relay has no controllers.'}`,
  };
}

/**
 * The current value of some symbols: open the live stream, take its SNAPSHOT frame, close.
 *
 * The platform's streams open with a snapshot and continue with deltas, so the first frame is
 * exactly "what these are right now" — the same fact the grid paints. Reading it this way rather
 * than adding a one-shot RPC means there is no second path to current values that could disagree
 * with the one the screen uses.
 */
async function readCurrent(
  backend: BackendClient,
  gatewayId: string,
  controllerId: string,
  paths: string[],
  timeoutMs = 4000,
): Promise<Map<string, unknown>> {
  const port = await backend.api.subscribeGridValues(gatewayId, controllerId, paths);
  return new Promise((resolve) => {
    const out = new Map<string, unknown>();
    const done = (): void => {
      try {
        port.postMessage({ kind: 'close' });
        // Close THIS end too. The stream is finished either way, and a port left open holds a
        // reference for as long as the page lives — one per read.
        port.close();
      } catch {
        /* the port may already be gone; closing twice is not an error worth reporting */
      }
      resolve(out);
    };
    // A stream that never produces a snapshot must not hang a tool call: the model gets what
    // arrived, and says so, rather than the turn stalling on a controller that is offline.
    const timer = setTimeout(done, timeoutMs);
    /*
     * THE SNAPSHOT ARRIVES IN PIECES, and the first piece is not the answer.
     *
     * This used to resolve on the first `snapshot` frame. Observed live against a controller with
     * every value on screen: frame one carried ONE symbol (whatever the warm cache already held),
     * frame two carried the other three 21 ms later — after this had already closed the port. The
     * tool then reported "1 of 12 read" beside a grid showing all twelve, and the assistant, doing
     * the right thing with a wrong instrument, told the person the controller was not polling them.
     *
     * So a snapshot is folded in and the read keeps listening until every path asked for has a
     * value, or the timeout says the rest are not coming. Delivery order and frame count are the
     * stream's business; "have I got everything I asked for" is the only question this can answer.
     */
    const wanted = new Set(paths);
    const complete = (): boolean => {
      for (const p of wanted) if (!out.has(p)) return false;
      return true;
    };
    port.onmessage = (e: MessageEvent<StreamFrame<TypedValue>>) => {
      const frame = e.data;
      if (frame.kind === 'snapshot') {
        for (const v of frame.items) out.set(v.symbolId, v.value);
      } else if (frame.kind === 'delta' && frame.op === 'upsert') {
        out.set(frame.item.symbolId, frame.item.value);
      } else if (frame.kind === 'error') {
        clearTimeout(timer);
        done();
        return;
      }
      if (complete()) {
        clearTimeout(timer);
        done();
      }
    };
    port.start();
  });
}

export function registerSymbolTools(deps: SymbolToolDeps): () => void {
  const search: ClientTool = {
    name: 'symbols.search',
    // reads the symbol catalogue.
    permission: Permission.SYMBOL_VIEW,
    description:
      "Find symbols (PLC tags) on a controller by name. Returns each one's path, data type, whether it is readable or writable, " +
      'and whether it is write-LOCKED. Use this before reading or writing anything — paths are exact, and guessing one wastes a turn.',
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        controller: { type: 'string', description: 'Controller id or name.' },
        query: {
          type: 'string',
          description: 'Text to match against the symbol path or label. Empty returns the first symbols on the controller.',
        },
      },
      required: ['controller'],
      additionalProperties: false,
    },
    run: async (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const c = findController(entry, String(args.controller ?? ''));
      if (!c) return controllerMiss(entry, String(args.controller ?? ''));
      const backend = deps.backend();
      if (!backend) return noBackend;
      const page = await backend.api.fetchSymbolPage(gw.value.id, c.id, { fts: String(args.query ?? ''), pageSize: MAX_ROWS });
      const rows = page.rows.map((r: (typeof page.rows)[number]) => ({
        path: r.path,
        dataType: r.dataType,
        permission: r.permission,
        locked: r.locked,
        label: r.label,
      }));
      const locked = rows.filter((r) => r.locked).length;
      return {
        ok: true,
        message:
          `${page.totalMatching} symbol${page.totalMatching === 1 ? '' : 's'} match on "${c.name}"` +
          `${page.totalMatching > rows.length ? `, showing the first ${rows.length}` : ''}.` +
          (locked ? ` ${locked} of these are write-locked and will refuse a write.` : ''),
        data: { controllerId: c.id, rows, totalMatching: page.totalMatching, shown: rows.length },
      };
    },
  };

  const read: ClientTool = {
    name: 'symbols.read',
    // reads live values.
    permission: Permission.SYMBOL_VIEW,
    description:
      'Read the current value of one or more symbols on a controller. Pass exact paths from symbols.search. ' +
      'Use it to answer what a machine is doing right now, or to check a value before and after a write.',
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        controller: { type: 'string', description: 'Controller id or name.' },
        paths: { type: 'array', items: { type: 'string' }, description: 'Exact symbol paths, as returned by symbols.search.' },
      },
      required: ['controller', 'paths'],
      additionalProperties: false,
    },
    run: async (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const c = findController(entry, String(args.controller ?? ''));
      if (!c) return controllerMiss(entry, String(args.controller ?? ''));
      const paths = (Array.isArray(args.paths) ? args.paths : []).map(String).filter(Boolean).slice(0, MAX_ROWS);
      if (!paths.length) return { ok: false, message: 'No paths given. Use symbols.search to find the exact paths first.' };
      const backend = deps.backend();
      if (!backend) return noBackend;
      const values = await readCurrent(backend, gw.value.id, c.id, paths);
      const rows = paths.map((p) => ({ path: p, value: values.has(p) ? values.get(p) : null }));
      const missing = rows.filter((r) => r.value === null).map((r) => r.path);
      return {
        ok: true,
        // A symbol that produced no value is NOT zero, and saying so is the difference between the
        // model reporting a reading and reporting a silence.
        message:
          `Read ${rows.length - missing.length} of ${rows.length} on "${c.name}".` +
          (missing.length
            ? ` No current value arrived for: ${missing.join(', ')} — the path may be wrong, or the controller may not be polling it.`
            : ''),
        data: { controllerId: c.id, values: rows },
      };
    },
  };

  const write: ClientTool = {
    name: 'symbols.write',
    // writes a value to the plant.
    permission: Permission.SYMBOL_WRITE,
    description:
      'Write values to symbols on a controller. This changes a running machine. ' +
      'Only writable, unlocked symbols accept a write — check with symbols.search first, and tell the user what you are about to change. ' +
      'A locked symbol is refused by the controller and you cannot unlock it; ask the person to.',
    mutates: true,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        controller: { type: 'string', description: 'Controller id or name.' },
        writes: {
          type: 'array',
          description: 'The symbols to write and the values to write to them.',
          items: {
            type: 'object',
            properties: {
              path: { type: 'string', description: 'Exact symbol path.' },
              value: { description: "The value: a number, a boolean, or a string, matching the symbol's data type." },
            },
            required: ['path', 'value'],
          },
        },
      },
      required: ['controller', 'writes'],
      additionalProperties: false,
    },
    run: async (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const c = findController(entry, String(args.controller ?? ''));
      if (!c) return controllerMiss(entry, String(args.controller ?? ''));
      const raw = Array.isArray(args.writes) ? args.writes : [];
      const wanted = raw
        .map((w) => w as { path?: unknown; value?: unknown })
        .filter((w) => typeof w.path === 'string' && w.path && w.value !== undefined)
        .map((w) => ({ path: String(w.path), value: w.value as string | number | boolean }));
      if (!wanted.length) return { ok: false, message: 'No writes given. Each entry needs a path and a value.' };

      try {
        const backend = deps.backend();
        if (!backend) return noBackend;
        /*
         * THE TYPE COMES FROM THE CATALOG, NOT FROM THE MODEL AND NOT FROM THE VALUE.
         *
         * This used to send `dataType: ''`, which the encoder reads as "string" — so 42.5 went to a
         * REAL symbol as the text "42.5" and the plugin refused it with `kind mismatch: got 4 want
         * 3`. Every numeric and boolean write the assistant ever attempted failed this way, and it
         * was invisible because the first live test also included a locked symbol, whose refusal is
         * the one this tool is designed to surface.
         *
         * Inferring from the JavaScript value would be wrong too: a model writing `1` to a BOOL or
         * `40` to a REAL is being helpful, not wrong, and the catalog knows what the symbol is. So
         * each path's vendor type is looked up and mapped through the SAME categorizer the grid's
         * value editor uses — one bridge between vendor names and the wire, not two.
         */
        const types = new Map<string, string>();
        for (const w of wanted) {
          if (types.has(w.path)) continue;
          const page = await backend.api.fetchSymbolPage(gw.value.id, c.id, { fts: w.path, pageSize: 50 });
          const hit = page.rows.find((r: { path: string }) => r.path === w.path);
          if (hit) types.set(w.path, categorize((hit as { dataType: string }).dataType));
        }
        const unknown = wanted.filter((w) => !types.has(w.path)).map((w) => w.path);
        if (unknown.length) {
          return {
            ok: false,
            message: `Not on "${c.name}": ${unknown.join(', ')}. Use symbols.search to find the exact path before writing.`,
          };
        }
        const writes = wanted.map((w) => ({ ...w, dataType: types.get(w.path)! }));
        const results = await backend.api.writeSymbols(gw.value.id, c.pluginId, c.id, writes);
        const failed = results.filter((r) => !r.ok);
        const lockedOnes = failed.filter((r) => r.errorCode === 'LOCKED').map((r) => r.symbolId);
        const other = failed.filter((r) => r.errorCode !== 'LOCKED');
        if (!failed.length) {
          return {
            ok: true,
            message: `Wrote ${results.length} symbol${results.length === 1 ? '' : 's'} on "${c.name}": ${writes.map((w) => `${w.path} = ${String(w.value)}`).join(', ')}.`,
            data: { controllerId: c.id, written: writes.length },
          };
        }
        return {
          ok: false,
          message:
            `${results.length - failed.length} of ${results.length} written on "${c.name}".` +
            // The LOCK is named separately because it is the one failure a person can clear, and the
            // assistant should say who has to do it rather than retrying.
            (lockedOnes.length ? ` Refused as write-LOCKED: ${lockedOnes.join(', ')} — a person has to unlock these; you cannot.` : '') +
            (other.length ? ` Refused: ${other.map((r) => `${r.symbolId} (${r.error || r.errorCode})`).join(', ')}.` : ''),
          data: {
            controllerId: c.id,
            locked: lockedOnes,
            failed: other.map((r) => ({ path: r.symbolId, error: r.error, code: r.errorCode })),
          },
        };
      } catch (e) {
        return { ok: false, message: `The relay refused that write: ${e instanceof Error ? e.message : String(e)}` };
      }
    },
  };

  /*
   * PROACTIVE LABELING — a permission with a tool behind it, finally.
   *
   * The role already held symbol:annotate; nothing could exercise it. A raw PLC path
   * ("DB1.DBX0.0", "MAIN.EL3174[2]") means nothing to a person reading a dashboard or a symbol
   * list — the label IS the bridge from that to "Tank Pressure (PSI)". Once a conversation has
   * established what a symbol actually controls (the user said so, or it was the only sensible
   * match for a name they used), set the label — do not wait to be asked, and do not require the
   * user to open the symbol tree and type it themselves for something you already know.
   */
  const label: ClientTool = {
    name: 'symbols.label',
    permission: Permission.SYMBOL_ANNOTATE,
    description:
      "Set a symbol's human-readable label — what a person sees instead of its raw path. Use this proactively once you " +
      'know what a symbol controls: after the user tells you, or after you resolve their name for it to one clear match. ' +
      'A well-labeled plant is more useful to everyone after your conversation, not just to you right now.',
    mutates: true,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        controller: { type: 'string', description: 'Controller id or name.' },
        path: { type: 'string', description: 'Exact symbol path, from symbols.search.' },
        label: { type: 'string', description: 'The human-readable name, e.g. "Tank 1 Pressure (PSI)".' },
      },
      required: ['controller', 'path', 'label'],
      additionalProperties: false,
    },
    run: async (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const c = findController(entry, String(args.controller ?? ''));
      if (!c) return controllerMiss(entry, String(args.controller ?? ''));
      const path = String(args.path ?? '').trim();
      const text = String(args.label ?? '').trim();
      if (!path) return { ok: false, message: 'path is required — an exact symbol path from symbols.search.' };
      const backend = deps.backend();
      if (!backend) return noBackend;
      try {
        const result = await backend.api.putLabels(gw.value.id, [{ controllerId: c.id, path, label: text }]);
        if (result.failed > 0) {
          return { ok: false, message: `Could not label "${path}" — the relay refused it.` };
        }
        return {
          ok: true,
          message: text ? `Labeled "${path}" as "${text}" on "${c.name}".` : `Cleared the label on "${path}" on "${c.name}".`,
        };
      } catch (e) {
        return { ok: false, message: `The relay refused that label: ${e instanceof Error ? e.message : String(e)}` };
      }
    },
  };

  return registerClientTools(search, read, write, label);
}
